import React, { useEffect, useState } from "react";
import { router, useForm } from "@inertiajs/react";
import PageHeader from "@/components/PageHeader";
import Button from "@/components/buttons/Button";
import Input from "@/components/inputs/Input";
import Checkbox from "@/components/inputs/Checkbox";
import Tag from "@/components/Tag";
import { withDisplayChrome } from "../Tracks/Contracts/contractChrome";
import {
  formatStatus,
  statusVariant,
} from "../Tracks/Contracts/contractStatus";
import FormActions from "@/components/buttons/FormActions";

interface Capacity {
  role: string;
  label: string;
}

interface SignProps {
  signatory_name: string;
  capacities: Capacity[];
  attestation_text: string;
  status: string;
  signable: boolean;
  expired: boolean;
  contract: {
    title: string;
    track_title: string;
    rendered_html: string;
  };
  sign_url: string;
  decline_url: string;
  back_url?: string | null;
}

const DeclineModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  declineUrl: string;
}> = ({ isOpen, onClose, declineUrl }) => {
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleDecline = () => {
    router.patch(
      declineUrl,
      {},
      {
        onStart: () => setProcessing(true),
        onSuccess: () => onClose(),
        onFinish: () => setProcessing(false),
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dark:bg-cream-900 mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="font-sans text-xl font-bold">Decline to sign</h2>
        <p className="text-cream-700 dark:text-cream-400 mt-2">
          Declining records that you will not sign this contract. This cannot be
          undone from this link — you would need Altar to re-issue it.
        </p>
        <FormActions className="mt-6">
          <Button
            text="Cancel"
            type="secondary"
            htmlButtonType="button"
            onClick={onClose}
            disabled={processing}
          />
          <Button
            text={processing ? "Declining..." : "Confirm decline"}
            type="danger-confirm"
            htmlButtonType="button"
            onClick={handleDecline}
            disabled={processing}
          />
        </FormActions>
      </div>
    </div>
  );
};

const Sign: React.FC<SignProps> = ({
  signatory_name,
  capacities,
  attestation_text,
  status,
  signable,
  expired,
  contract,
  sign_url,
  decline_url,
  back_url,
}) => {
  const [isDeclineOpen, setIsDeclineOpen] = useState(false);
  const { data, setData, patch, processing } = useForm({
    typed_name: "",
    attested: false,
  });

  const handleSign = (e: React.FormEvent) => {
    e.preventDefault();
    patch(sign_url);
  };

  const canSubmit =
    data.typed_name.trim() !== "" && data.attested && !processing;

  const statusLabel =
    status === "pending" && expired ? "Expired" : formatStatus(status);

  const helperLine =
    status === "declined"
      ? "You have declined to sign this contract."
      : status === "pending" && expired
        ? "This signing link has expired."
        : null;

  return (
    <div className="flex w-full flex-col items-start justify-start gap-5 md:gap-10">
      <div className="flex w-full flex-col items-start gap-4">
        <PageHeader
          title={`${contract.track_title} - ${contract.title}`}
          backLink={
            back_url ? { href: back_url, text: "Back to Contracts" } : undefined
          }
        />
        {status === "signed" && (
          <Tag label={formatStatus(status)} variant={statusVariant(status)} />
        )}
      </div>

      <div className="flex w-full flex-col gap-6">
        <iframe
          srcDoc={withDisplayChrome(contract.rendered_html)}
          title={contract.title}
          sandbox=""
          className="border-cream-200 dark:border-cream-800 h-[80vh] w-full rounded-2xl border bg-white"
        />

        {status !== "signed" && (
          <div className="md:dark:bg-cream-950 flex w-full flex-col gap-6 rounded-2xl md:bg-white md:p-10 md:shadow">
            <div className="flex w-full flex-col gap-2">
              <div className="flex flex-row flex-wrap items-center gap-3">
                <span className="text-cream-700 dark:text-cream-300">
                  Signing as{" "}
                  <span className="text-cream-900 dark:text-cream-100 font-semibold">
                    {signatory_name}
                  </span>
                </span>
                {capacities.map((capacity) => (
                  <Tag key={capacity.role} label={capacity.label} />
                ))}
                {!signable && (
                  <Tag label={statusLabel} variant={statusVariant(status)} />
                )}
              </div>
              {helperLine && (
                <p className="text-cream-600 dark:text-cream-400 text-sm">
                  {helperLine}
                </p>
              )}
            </div>

            {signable && (
              <form
                onSubmit={handleSign}
                className="flex w-full flex-col gap-6"
              >
                <div className="flex w-full flex-col gap-1.5">
                  <Input
                    type="text"
                    label="Full legal name *"
                    name="typed_name"
                    value={data.typed_name}
                    onChange={(e) => setData("typed_name", e.target.value)}
                    placeholder="Type your full name"
                  />
                  <p className="text-cream-600 dark:text-cream-400 text-sm">
                    This is how your signature will appear on the contract.
                  </p>
                </div>
                <Checkbox
                  checked={data.attested}
                  onChange={(e) => setData("attested", e.target.checked)}
                  checkboxLabel={attestation_text}
                />
                <FormActions>
                  <Button
                    text="Decline"
                    type="danger"
                    htmlButtonType="button"
                    onClick={() => setIsDeclineOpen(true)}
                    disabled={processing}
                  />
                  <Button
                    text={processing ? "Signing..." : "Sign contract"}
                    type="primary"
                    htmlButtonType="submit"
                    disabled={!canSubmit}
                  />
                </FormActions>
              </form>
            )}
          </div>
        )}
      </div>

      <DeclineModal
        isOpen={isDeclineOpen}
        onClose={() => setIsDeclineOpen(false)}
        declineUrl={decline_url}
      />
    </div>
  );
};

export default Sign;
